import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';

const Modal = ({ title, onClose, onSubmit, children, width = 480 }) => {
  const submitRef = useRef(onSubmit);
  useEffect(() => { submitRef.current = onSubmit; });
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'Enter' && submitRef.current) {
        const tag = document.activeElement?.tagName?.toLowerCase();
        if (tag !== 'textarea' && tag !== 'select' && tag !== 'button') {
          e.preventDefault();
          submitRef.current();
        }
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-panel" style={{ maxWidth: width }}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}>
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
};

const EyeIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOffIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24M1 1l22 22"/>
  </svg>
);
const PasswordField = ({ value, onChange, placeholder, disabled }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="pwd-wrap">
      <input className="field-input" type={show ? 'text' : 'password'}
        placeholder={placeholder} value={value} onChange={onChange} disabled={disabled} />
      <button type="button" className="pwd-toggle" onClick={() => setShow(s => !s)} tabIndex={-1}>
        {show ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
};

const ProfileModal = ({ onClose }) => {
  const { user, editProfile } = useAuth();
  const [tab, setTab] = useState('info');

  // Info tab
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [infoSaving, setInfoSaving] = useState(false);
  const [infoError, setInfoError] = useState('');
  const [infoSuccess, setInfoSuccess] = useState('');

  // Password tab
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdError, setPwdError] = useState('');
  const [pwdSuccess, setPwdSuccess] = useState('');

  const handleSaveInfo = async () => {
    if (infoSaving) return;
    if (!name.trim()) { setInfoError('Le nom est requis.'); return; }
    setInfoSaving(true); setInfoError(''); setInfoSuccess('');
    try {
      await editProfile({ name: name.trim(), email: email.trim() });
      setInfoSuccess('Profil mis à jour avec succès.');
    } catch (e) {
      setInfoError(e?.response?.data?.message || 'Erreur lors de la mise à jour.');
    } finally {
      setInfoSaving(false);
    }
  };

  const handleSavePwd = async () => {
    if (pwdSaving) return;
    if (!currentPwd) { setPwdError('Le mot de passe actuel est requis.'); return; }
    if (newPwd.length < 6) { setPwdError('Le nouveau mot de passe doit faire au moins 6 caractères.'); return; }
    if (newPwd !== confirmPwd) { setPwdError('Les mots de passe ne correspondent pas.'); return; }
    setPwdSaving(true); setPwdError(''); setPwdSuccess('');
    try {
      await authAPI.changePassword({ currentPassword: currentPwd, newPassword: newPwd });
      setPwdSuccess('Mot de passe modifié avec succès.');
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
    } catch (e) {
      setPwdError(e?.response?.data?.message || 'Erreur lors du changement de mot de passe.');
    } finally {
      setPwdSaving(false);
    }
  };

  return (
    <Modal title="Mon profil" onClose={onClose} onSubmit={tab === 'info' ? handleSaveInfo : handleSavePwd} width={460}>
      <div className="modal-tabs">
        <button className={`modal-tab${tab === 'info' ? ' active' : ''}`} onClick={() => setTab('info')}>
          Informations
        </button>
        <button className={`modal-tab${tab === 'password' ? ' active' : ''}`} onClick={() => setTab('password')}>
          Mot de passe
        </button>
      </div>

      {tab === 'info' && (
        <div className="modal-fields">
          <div className="field-group">
            <label className="field-label">Nom</label>
            <input className="field-input" type="text" value={name}
              onChange={e => setName(e.target.value)} placeholder="Votre nom" />
          </div>
          <div className="field-group">
            <label className="field-label">Adresse email</label>
            <input className="field-input" type="email" value={email}
              onChange={e => setEmail(e.target.value)} placeholder="votre@email.com" />
          </div>
          {infoError && <div className="modal-error">{infoError}</div>}
          {infoSuccess && <div className="modal-success">{infoSuccess}</div>}
          <div className="modal-actions">
            <button className="btn-ghost" onClick={onClose}>Fermer</button>
            <button className="btn-primary" onClick={handleSaveInfo} disabled={infoSaving}>
              {infoSaving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </div>
      )}

      {tab === 'password' && (
        <div className="modal-fields">
          <div className="field-group">
            <label className="field-label">Mot de passe actuel</label>
            <PasswordField value={currentPwd} onChange={e => setCurrentPwd(e.target.value)}
              placeholder="••••••••" disabled={pwdSaving} />
          </div>
          <div className="field-group">
            <label className="field-label">Nouveau mot de passe <span className="field-opt">6 caractères min.</span></label>
            <PasswordField value={newPwd} onChange={e => setNewPwd(e.target.value)}
              placeholder="••••••••" disabled={pwdSaving} />
          </div>
          <div className="field-group">
            <label className="field-label">Confirmer le nouveau mot de passe</label>
            <PasswordField value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)}
              placeholder="••••••••" disabled={pwdSaving} />
          </div>
          {pwdError && <div className="modal-error">{pwdError}</div>}
          {pwdSuccess && <div className="modal-success">{pwdSuccess}</div>}
          <div className="modal-actions">
            <button className="btn-ghost" onClick={onClose}>Fermer</button>
            <button className="btn-primary" onClick={handleSavePwd} disabled={pwdSaving}>
              {pwdSaving ? 'Modification...' : 'Changer le mot de passe'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ProfileModal;
